// Tiny markdown-lite renderer for super-admin-authored policy page text
// (Terms/Privacy/Shipping/Refund/Return/Cancellation). Deliberately does NOT
// accept raw HTML/dangerouslySetInnerHTML — every element below is a real
// React element we construct ourselves, so admin-authored text can never
// inject a script tag regardless of how much we trust the author.
//
// Supported syntax, one block per blank-line-separated chunk:
//   ## Heading            -> <h2>
//   - bullet item          -> <ul><li>
//   1. numbered item        -> <ol><li> (any leading digits + ". " counts)
//   > callout text          -> highlighted note box (used for the one-off
//                              "shipped orders can't be cancelled" style box)
//   **bold**                -> <strong>, inline, anywhere in a line
//   [label](url)            -> link, inline; internal (starts with "/") uses
//                              react-router Link, external opens a new tab
import { Link } from "react-router-dom";

function renderInline(text, keyPrefix) {
  const nodes = [];
  const re = /\*\*(.+?)\*\*|\[([^\]]+)\]\(([^)]+)\)/g;
  let last = 0;
  let match;
  let i = 0;
  while ((match = re.exec(text))) {
    if (match.index > last) nodes.push(text.slice(last, match.index));
    if (match[1] !== undefined) {
      nodes.push(<strong key={`${keyPrefix}-b${i++}`}>{match[1]}</strong>);
    } else {
      const label = match[2];
      const href = match[3];
      nodes.push(
        href.startsWith("/")
          ? <Link key={`${keyPrefix}-l${i++}`} to={href} className="text-[#556B2F] font-semibold hover:underline">{label}</Link>
          : (
            <a
              key={`${keyPrefix}-l${i++}`}
              href={href}
              className="text-[#556B2F] font-semibold hover:underline"
              target={href.startsWith("http") ? "_blank" : undefined}
              rel={href.startsWith("http") ? "noreferrer" : undefined}
            >
              {label}
            </a>
          )
      );
    }
    last = re.lastIndex;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

export function renderPolicyContent(raw) {
  const lines = String(raw || "").replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let para = [];
  let list = null;
  let quote = [];

  const flushPara  = () => { if (para.length) { blocks.push({ type: "p", text: para.join(" ") }); para = []; } };
  const flushList  = () => { if (list) { blocks.push(list); list = null; } };
  const flushQuote = () => { if (quote.length) { blocks.push({ type: "quote", text: quote.join(" ") }); quote = []; } };

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (line === "") { flushPara(); flushList(); flushQuote(); continue; }

    if (line.startsWith("## ")) {
      flushPara(); flushList(); flushQuote();
      blocks.push({ type: "h2", text: line.slice(3) });
    } else if (line.startsWith("- ")) {
      flushPara(); flushQuote();
      if (!list || list.type !== "ul") { flushList(); list = { type: "ul", items: [] }; }
      list.items.push(line.slice(2));
    } else if (/^\d+\.\s/.test(line)) {
      flushPara(); flushQuote();
      if (!list || list.type !== "ol") { flushList(); list = { type: "ol", items: [] }; }
      list.items.push(line.replace(/^\d+\.\s/, ""));
    } else if (line.startsWith("> ")) {
      flushPara(); flushList();
      quote.push(line.slice(2));
    } else {
      flushList(); flushQuote();
      para.push(line);
    }
  }
  flushPara(); flushList(); flushQuote();

  return blocks.map((b, i) => {
    const key = `pb-${i}`;
    if (b.type === "h2") {
      return <h2 key={key} className="font-display text-xl font-bold text-[#5A2D1D] mt-8 mb-1">{renderInline(b.text, key)}</h2>;
    }
    if (b.type === "ul") {
      return <ul key={key} className="list-disc pl-5 space-y-2 mt-2">{b.items.map((it, j) => <li key={j}>{renderInline(it, `${key}-${j}`)}</li>)}</ul>;
    }
    if (b.type === "ol") {
      return <ol key={key} className="list-decimal pl-5 space-y-3">{b.items.map((it, j) => <li key={j}>{renderInline(it, `${key}-${j}`)}</li>)}</ol>;
    }
    if (b.type === "quote") {
      return (
        <p key={key} className="bg-[#5A2D1D]/10 border border-[#5A2D1D]/20 rounded-sm px-4 py-3 text-sm">
          {renderInline(b.text, key)}
        </p>
      );
    }
    return <p key={key}>{renderInline(b.text, key)}</p>;
  });
}
